<template>
  <div class="callback-container">
    <div class="callback-card">
      <div v-if="!errorMessage" class="loading-section">
        <WLoader color="dark" :size="64" section-tag="auth-callback-loader" class="mb-4" />
        <h2 class="callback-title">Completing Sign-in...</h2>
        <p class="callback-subtitle">Please wait while we process your authentication</p>
      </div>

      <div v-if="errorMessage" class="error-section">
        <ErrorState
          headline="Authentication Error"
          :body="errorMessage"
          section-tag="auth-callback-error"
        />

        <WButton label="Return to Login" color="primary" section-tag="auth-callback-return" @click="returnToLogin" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'

import { useAuthStore } from '@/stores/auth.store'

import { useAuth } from '@/composables/useAuth'

import WButton from '@wasabi/actions/WButton.vue'
import ErrorState from '@wasabi/patterns/ErrorState.vue'
import WLoader from '@wasabi/data-visualization/WLoader.vue'

// ============ ROUTER ============
const router = useRouter()

// ============ STORES ============
const authStore = useAuthStore()

// ============ COMPOSABLES ============
const auth = useAuth()

// ============ REACTIVE STATE ============
const errorMessage = ref('')

// ============ METHODS / EVENT HANDLERS ============
const returnToLogin = () => {
  router.push('/admin/login')
}

// ============ LIFECYCLE HOOKS ============
onMounted(async () => {
  try {
    const session = await auth.handleCallback()

    if (session) {
      const isAuthed = await authStore.checkAuth()
      if (isAuthed) {
        // Clear the callback URL parameters
        window.history.replaceState({}, document.title, '/adx/auth/callback')
        await router.push('/admin/dashboard')
        return
      }
    }

    errorMessage.value = 'Authentication failed. Please try again.'
  } catch {
    errorMessage.value = 'An unexpected error occurred during authentication.'
  }
})
</script>

<style scoped>
.callback-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 20px;
}

.callback-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  padding: 40px;
  width: 100%;
  max-width: 400px;
  text-align: center;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.loading-section {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.callback-title {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 8px 0;
  line-height: 1.2;
}

.callback-subtitle {
  font-size: 14px;
  color: #666;
  margin: 0;
  font-weight: 400;
}

.error-section {
  margin-top: 24px;
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .callback-container {
    padding: 16px;
  }

  .callback-card {
    padding: 24px;
    max-width: none;
  }

  .callback-title {
    font-size: 20px;
  }
}
</style>
